// The installers a PC is set up with (F-STN-01, F-STN-17; spec/10 §3.1, §6.5): Clavian Station for a gaming PC and
// Clavian Counter for the front-desk PC, each a plain download from the release the venue's channel settles on
// (`GET …/station-installers`). The owner, 2026-10-01: "I don't expect clients to download and install via CLI" — so a
// PC is set up by downloading and double-clicking, never by a command line. Shown in the Stations tab's "Download apps"
// dialog, in the pairing dialog, and in the printers' "pair a Clavian Counter PC first" notice (Counter only).
//
// 2026-10-14: the default download is ONE branded setup exe for either kind of PC (the person chooses a gaming PC or
// the front desk in its window); the two MSIs are folded under "For IT: silent install packages" with their command
// lines. A release cut before the setup exe existed has none, so the MSIs are then offered as before.

import type { StationReleaseFile } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Button, Skeleton } from '@clavian/ui-web';
import type { ReactNode } from 'react';

import { Icon } from '../shell/nav-icons';
import { useStationInstallers } from './queries';

import './installers.css';

type Flavor = 'station' | 'counter';

export function InstallerDownloads({
  organizationId,
  venueId,
  only,
}: {
  readonly organizationId: string;
  readonly venueId: string;
  /** Offer just one installer (the printers' notice offers only Clavian Counter). */
  readonly only?: Flavor;
}): ReactNode {
  const { t } = useTranslation('console');
  const installers = useStationInstallers(organizationId, venueId);
  if (installers.data === undefined) {
    return installers.isError ? <p className="console-subtle">{t('stations.installers.unavailable')}</p> : <Skeleton shape="text" lines={2} />;
  }
  const { version, station, counter, setup } = installers.data;
  if (version === null) return <p className="console-subtle">{t('stations.installers.none')}</p>;
  const flavors: readonly Flavor[] = only === undefined ? ['station', 'counter'] : [only];
  if (setup === null || setup === undefined) {
    return (
      <ul className="console-installers" aria-label={t('stations.installers.label')}>
        {flavors.map((flavor) => (
          <Installer key={flavor} flavor={flavor} version={version} file={flavor === 'station' ? station : counter} />
        ))}
      </ul>
    );
  }
  return (
    <div className="console-installers-group">
      <ul className="console-installers" aria-label={t('stations.installers.label')}>
        <Installer flavor="setup" version={version} file={setup} counterOnly={only === 'counter'} />
      </ul>
      {only === undefined ? <ItPackages version={version} setup={setup} station={station} counter={counter} /> : null}
    </div>
  );
}

/** The MSIs for IT, imaging and silent installs, folded away: a person setting up one PC never needs them. */
function ItPackages({
  version,
  setup,
  station,
  counter,
}: {
  readonly version: string;
  readonly setup: StationReleaseFile;
  readonly station: StationReleaseFile | null;
  readonly counter: StationReleaseFile | null;
}): ReactNode {
  const { t } = useTranslation('console');
  const fileName = (file: StationReleaseFile): string => file.url.slice(file.url.lastIndexOf('/') + 1);
  const commands = [
    `${fileName(setup)} /quiet Role=station`,
    `${fileName(setup)} /quiet Role=counter`,
    ...(station === null ? [] : [`msiexec /i ${fileName(station)} /qn`]),
    ...(counter === null ? [] : [`msiexec /i ${fileName(counter)} /qn`]),
  ];
  return (
    <details className="console-installers__it">
      <summary>{t('stations.installers.it.summary')}</summary>
      <p className="console-subtle">{t('stations.installers.it.hint')}</p>
      <ul className="console-installers" aria-label={t('stations.installers.it.label')}>
        <Installer flavor="station" version={version} file={station} />
        <Installer flavor="counter" version={version} file={counter} />
      </ul>
      <p className="console-subtle">{t('stations.installers.it.commands')}</p>
      <pre className="console-installers__commands">{commands.join('\n')}</pre>
    </details>
  );
}

function Installer({
  flavor,
  version,
  file,
  counterOnly = false,
}: {
  readonly flavor: Flavor | 'setup';
  readonly version: string;
  readonly file: StationReleaseFile | null;
  /** The setup exe offered for the front-desk PC alone (the printers' notice): says to choose Front desk PC. */
  readonly counterOnly?: boolean;
}): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  return (
    <li className="console-installer">
      <div className="console-installer__text">
        <span className="console-installer__title">{t(`stations.installers.${flavor}.title`)}</span>
        <span className="console-subtle">
          {flavor === 'setup' && counterOnly ? t('stations.installers.setup.hintCounter') : t(`stations.installers.${flavor}.hint`)}
        </span>
        {file === null ? null : (
          <span className="console-subtle console-num">
            {t('stations.installers.version', {
              version,
              size: format.number(file.size / 1_048_576, { style: 'unit', unit: 'megabyte', maximumFractionDigits: 1 }),
            })}
          </span>
        )}
      </div>
      {file === null ? (
        <span className="console-subtle">{t('stations.installers.counterNone')}</span>
      ) : (
        <Button variant={flavor === 'counter' ? 'secondary' : 'primary'} size="sm" asChild>
          <a href={file.url} download>
            <span className="clav-button__icon">
              <Icon name="download" />
            </span>
            {t(`stations.installers.${flavor}.download`)}
          </a>
        </Button>
      )}
    </li>
  );
}
